import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useT } from "../i18n/LocaleProvider";
import { LockOverlay } from "./LockOverlay";
import { FullscreenGate } from "./FullscreenGate";
import { ExitCodePrompt } from "./ExitCodePrompt";

// Parental controls. A 4-digit PIN (set by the grown-up) gates the settings and
// unlocking. Usage is counted per day and persisted; when it reaches the daily
// limit the whole app is dimmed + made unclickable by LockOverlay. Optionally a
// "keep full screen" lock keeps the child in fullscreen — exiting it shows a
// gate that needs the PIN (or a tap to return to fullscreen).
const PIN_KEY = "learn-draw:pin";
// When the current PIN was set (ms epoch) and how many wrong tries since the
// last correct entry. The PIN always self-clears PIN_TTL_MS after it was set,
// and after WRONG_TRIES_FOR_HINT misses we reveal a hint (first digit + digit
// sum) so a grown-up who forgot can still get in.
const PIN_SET_KEY = "learn-draw:pinSetAt";
const PIN_TRIES_KEY = "learn-draw:pinTries";
const PIN_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours
const WRONG_TRIES_FOR_HINT = 3;
const LIMIT_KEY = "learn-draw:limitMin";
const USAGE_KEY = "learn-draw:usage";
const FS_KEY = "learn-draw:fsLock";
const FEW_KEY = "learn-draw:fewScreen";

// Cross-browser fullscreen helpers.
type FsDoc = Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => void };
type FsEl = HTMLElement & { webkitRequestFullscreen?: () => void };

function fullscreenEl(): Element | null {
  const d = document as FsDoc;
  return document.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}
function fullscreenSupported(): boolean {
  const el = document.documentElement as FsEl;
  return !!(el.requestFullscreen || el.webkitRequestFullscreen);
}
function requestFullscreen(): void {
  const el = document.documentElement as FsEl;
  const fn = el.requestFullscreen ?? el.webkitRequestFullscreen;
  if (!fn) return;
  try {
    const r = fn.call(el) as unknown as Promise<void> | undefined;
    if (r && typeof r.catch === "function") r.catch(() => {});
  } catch {
    /* ignore */
  }
}
function exitFullscreen(): void {
  const d = document as FsDoc;
  const fn = document.exitFullscreen ?? d.webkitExitFullscreen;
  if (fullscreenEl() && fn) {
    try {
      fn.call(document);
    } catch {
      /* ignore */
    }
  }
}

// Keyboard Lock API (Chromium): while fullscreen, capture Escape so a single
// press no longer exits fullscreen — it's delivered to us instead. Lets us
// require a double-press before showing the exit-code prompt.
type KbNav = Navigator & { keyboard?: { lock?: (keys: string[]) => Promise<void>; unlock?: () => void } };
function lockEscape(): void {
  try {
    (navigator as KbNav).keyboard?.lock?.(["Escape"])?.catch(() => {});
  } catch {
    /* ignore */
  }
}
function unlockKeyboard(): void {
  try {
    (navigator as KbNav).keyboard?.unlock?.();
  } catch {
    /* ignore */
  }
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function loadUsage(): { date: string; used: number } {
  try {
    const raw = localStorage.getItem(USAGE_KEY);
    if (raw) {
      const u = JSON.parse(raw) as { date: string; used: number };
      if (u && u.date === todayStr()) return { date: u.date, used: Number(u.used) || 0 };
    }
  } catch {
    /* ignore */
  }
  return { date: todayStr(), used: 0 };
}

// Load the PIN, honouring the 2-hour self-clear. A PIN saved before this
// feature existed has no timestamp — stamp it "now" so it gets a full 2 hours
// from first load rather than being treated as already expired.
function loadPin(): string | null {
  try {
    const p = localStorage.getItem(PIN_KEY);
    if (!p) return null;
    const setAt = Number(localStorage.getItem(PIN_SET_KEY));
    if (!setAt) {
      localStorage.setItem(PIN_SET_KEY, String(Date.now()));
      return p;
    }
    if (Date.now() - setAt >= PIN_TTL_MS) {
      localStorage.removeItem(PIN_KEY);
      localStorage.removeItem(PIN_SET_KEY);
      localStorage.removeItem(PIN_TRIES_KEY);
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

function loadTries(): number {
  return Number(localStorage.getItem(PIN_TRIES_KEY)) || 0;
}

const digitSum = (pin: string): number =>
  pin.split("").reduce((s, c) => s + (Number(c) || 0), 0);

interface TimeLimitValue {
  hasPin: boolean;
  /** Daily limit in minutes; 0 means no limit. */
  limitMin: number;
  usedSec: number;
  remainingSec: number;
  locked: boolean;
  setPin: (pin: string) => void;
  /** Remove the grown-up code entirely (settings become unlocked). */
  clearPin: () => void;
  verifyPin: (pin: string) => boolean;
  /** Record the result of a PIN entry so wrong tries accrue toward the hint.
   *  Call once per completed entry with whether it was correct. */
  registerPinAttempt: (ok: boolean) => void;
  /** A recovery hint (first digit + digit sum) once there have been enough
   *  wrong tries; null before that or when no PIN is set. */
  pinHint: string | null;
  setLimit: (min: number) => void;
  /** Give back today's time so the child can keep going (used to unlock). */
  resetToday: () => void;
  /** "Keep full screen" parental lock. */
  fsLock: boolean;
  fsSupported: boolean;
  setFsLock: (on: boolean) => void;
  enterFullscreen: () => void;
  /**
   * "Fewer distractions": hide the footer links (Privacy, Say hello, Buy me a
   * coffee…) and the Print-to-color button so the child sees only the drawings.
   * Effective value — already folds in the auto-on default below.
   */
  fewScreen: boolean;
  setFewScreen: (on: boolean) => void;
}

const TimeLimitContext = createContext<TimeLimitValue | null>(null);

export function useTimeLimit(): TimeLimitValue {
  const ctx = useContext(TimeLimitContext);
  if (!ctx) throw new Error("useTimeLimit must be used within a TimeLimitProvider");
  return ctx;
}

export function TimeLimitProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [pin, setPinState] = useState<string | null>(loadPin);
  const [wrongTries, setWrongTries] = useState<number>(loadTries);
  const [limitMin, setLimitState] = useState<number>(
    () => Number(localStorage.getItem(LIMIT_KEY)) || 0,
  );
  const [usedSec, setUsedSec] = useState<number>(() => loadUsage().used);
  const dateRef = useRef<string>(todayStr());

  // Count one second of usage while the tab is visible; reset at midnight.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const d = todayStr();
      if (d !== dateRef.current) {
        dateRef.current = d;
        setUsedSec(0);
      } else {
        setUsedSec((s) => s + 1);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  // Persist usage (throttled to every 5s, plus whenever the tab is hidden).
  useEffect(() => {
    if (usedSec % 5 !== 0) return;
    try {
      localStorage.setItem(USAGE_KEY, JSON.stringify({ date: dateRef.current, used: usedSec }));
    } catch {
      /* ignore */
    }
  }, [usedSec]);

  useEffect(() => {
    const persist = () => {
      try {
        localStorage.setItem(USAGE_KEY, JSON.stringify({ date: dateRef.current, used: usedSec }));
      } catch {
        /* ignore */
      }
    };
    document.addEventListener("visibilitychange", persist);
    window.addEventListener("pagehide", persist);
    return () => {
      document.removeEventListener("visibilitychange", persist);
      window.removeEventListener("pagehide", persist);
    };
  }, [usedSec]);

  const setPin = useCallback((p: string) => {
    setPinState(p);
    setWrongTries(0);
    try {
      localStorage.setItem(PIN_KEY, p);
      localStorage.setItem(PIN_SET_KEY, String(Date.now()));
      localStorage.removeItem(PIN_TRIES_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const clearPin = useCallback(() => {
    setPinState(null);
    setWrongTries(0);
    try {
      localStorage.removeItem(PIN_KEY);
      localStorage.removeItem(PIN_SET_KEY);
      localStorage.removeItem(PIN_TRIES_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const verifyPin = useCallback((p: string) => pin != null && p === pin, [pin]);

  // Track wrong tries so the hint can appear; a correct entry clears the count.
  const registerPinAttempt = useCallback((ok: boolean) => {
    setWrongTries((n) => {
      const next = ok ? 0 : n + 1;
      try {
        if (next === 0) localStorage.removeItem(PIN_TRIES_KEY);
        else localStorage.setItem(PIN_TRIES_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // The grown-up code self-clears 2 hours after it was set — regardless of any
  // attempts. A timer fires at the exact expiry; a visibility re-check catches
  // the case where the tab was backgrounded (timers there can be throttled).
  useEffect(() => {
    if (pin == null) return;
    const setAt = Number(localStorage.getItem(PIN_SET_KEY)) || Date.now();
    const check = () => {
      if (Date.now() - setAt >= PIN_TTL_MS) clearPin();
    };
    check();
    const t = window.setTimeout(clearPin, Math.max(0, PIN_TTL_MS - (Date.now() - setAt)));
    document.addEventListener("visibilitychange", check);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("visibilitychange", check);
    };
  }, [pin, clearPin]);

  const setLimit = useCallback((min: number) => {
    setLimitState(min);
    try {
      localStorage.setItem(LIMIT_KEY, String(min));
    } catch {
      /* ignore */
    }
  }, []);

  const resetToday = useCallback(() => setUsedSec(0), []);

  // "Keep full screen" lock.
  const fsSupported = fullscreenSupported();
  const [fsLock, setFsLockState] = useState<boolean>(() => localStorage.getItem(FS_KEY) === "1");
  const [isFullscreen, setIsFullscreen] = useState<boolean>(() => !!fullscreenEl());
  // Shown (while staying fullscreen) when the child double-presses Escape.
  const [exitPrompt, setExitPrompt] = useState(false);
  // Brief "press Esc again" hint after a single Escape.
  const [escHint, setEscHint] = useState(false);
  const lastEscRef = useRef(0);

  // Track fullscreen; when entering with the lock on, grab the keyboard so
  // Escape stops auto-exiting (Chromium). Release + clear prompts on exit.
  useEffect(() => {
    const onChange = () => {
      const fs = !!fullscreenEl();
      setIsFullscreen(fs);
      if (fs && fsLock) lockEscape();
      if (!fs) {
        unlockKeyboard();
        setExitPrompt(false);
        setEscHint(false);
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, [fsLock]);

  // Double-press Escape (while locked + fullscreen) opens the exit-code prompt.
  // This only fires where Keyboard Lock keeps us in fullscreen; otherwise the
  // browser exits on the first Escape and the FullscreenGate handles it.
  useEffect(() => {
    if (!fsLock) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !fullscreenEl()) return;
      e.preventDefault();
      const now = Date.now();
      if (now - lastEscRef.current < 700) {
        lastEscRef.current = 0;
        setEscHint(false);
        setExitPrompt(true);
      } else {
        lastEscRef.current = now;
        setEscHint(true);
        window.setTimeout(() => setEscHint(false), 1500);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [fsLock]);

  const setFsLock = useCallback((on: boolean) => {
    setFsLockState(on);
    try {
      localStorage.setItem(FS_KEY, on ? "1" : "0");
    } catch {
      /* ignore */
    }
    // Called from a click (user gesture), so requesting fullscreen is allowed.
    if (on) {
      requestFullscreen();
    } else {
      unlockKeyboard();
      exitFullscreen();
      setExitPrompt(false);
    }
  }, []);

  const enterFullscreen = useCallback(() => requestFullscreen(), []);

  // "Fewer distractions". Stored as a tri-state: a grown-up's explicit choice
  // (true/false) wins; when they've never touched it (null), it follows the
  // focus modes — on by default whenever a time limit or the full-screen lock
  // is active, off otherwise.
  const [fewPref, setFewPref] = useState<boolean | null>(() => {
    const raw = localStorage.getItem(FEW_KEY);
    return raw === "1" ? true : raw === "0" ? false : null;
  });
  const setFewScreen = useCallback((on: boolean) => {
    setFewPref(on);
    try {
      localStorage.setItem(FEW_KEY, on ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  const pinHint =
    pin != null && wrongTries >= WRONG_TRIES_FOR_HINT
      ? t.pinHint(pin[0], digitSum(pin))
      : null;

  const remainingSec = limitMin > 0 ? Math.max(0, limitMin * 60 - usedSec) : Infinity;
  const locked = limitMin > 0 && usedSec >= limitMin * 60;
  // Only enforce fullscreen where the browser supports it (graceful degrade).
  const fsBlocked = fsLock && fsSupported && !isFullscreen;
  const fewScreen = fewPref ?? (fsLock || limitMin > 0);

  const value: TimeLimitValue = {
    hasPin: pin != null,
    limitMin,
    usedSec,
    remainingSec,
    locked,
    setPin,
    clearPin,
    verifyPin,
    registerPinAttempt,
    pinHint,
    setLimit,
    resetToday,
    fsLock,
    fsSupported,
    setFsLock,
    enterFullscreen,
    fewScreen,
    setFewScreen,
  };

  return (
    <TimeLimitContext.Provider value={value}>
      <div className={locked || fsBlocked || exitPrompt ? "app-dim" : undefined}>{children}</div>
      {escHint && !exitPrompt && <div className="esc-hint">{t.tlEscHint}</div>}
      {locked ? (
        <LockOverlay />
      ) : fsBlocked ? (
        <FullscreenGate />
      ) : exitPrompt ? (
        <ExitCodePrompt onCancel={() => setExitPrompt(false)} />
      ) : null}
    </TimeLimitContext.Provider>
  );
}
